import { useState, useEffect, useRef } from "react";
import {
  mx,
  eroticResponses,
  neutralResponses,
  howAreYouResponses,
} from "../data/messages";
import { getRandomDelay, wait } from "../utils/delay";
import {
  isGibberish,
  containsPhotoKeyword,
  isHowAreYou,
} from "../utils/messageUtils";
import {
  simulateTyping,
  display,
  getRandomFromArray,
  photoResponses
} from "../utils/chatUtils";
import {
  extractTokenFromUrl,
  validateToken,
  unlockMessages
} from "../utils/photoUtils";
import {
  getProfilIdFromUrlName,
  getProfil,
  setFirstMessageAvatar,
  generateUnlockLink
} from "../utils/chatProfileUtils";

const CHAT_STATE_KEY = "chatState";

// Délai plus long pour le "temps de réflexion" du bot
const getBotThinkingDelay = () => Math.floor(Math.random() * (3000 - 1200) + 1200);

export function useChat() {
  // --- ÉTAT DU CHAT ---
  const saved = JSON.parse(localStorage.getItem(CHAT_STATE_KEY)) || {};
  const init = {
    messages: saved.messages || [],
    idx: saved.currentIndex || 0,
    started: saved.initialFlowStarted || false,
  };

  const [messages, setMessages] = useState(init.messages);
  const [isTyping, setIsTyping] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(init.idx);
  const initialFlowStarted = useRef(init.started);
  const tokenProcessed = useRef(false); // Pour éviter de traiter le token plusieurs fois

  // --- PERSISTANCE DU CHAT ---
  useEffect(() => {
    localStorage.setItem(
      CHAT_STATE_KEY,
      JSON.stringify({
        messages,
        currentIndex,
        initialFlowStarted: initialFlowStarted.current,
      })
    );
  }, [messages, currentIndex]);

  // --- DYNAMISATION DE LA PREMIÈRE PHOTO DU CHAT SELON LE PROFIL ---
  const profilId = getProfilIdFromUrlName();
  const profil = getProfil(profilId);
  setFirstMessageAvatar(mx, profil);

  // --- DÉBLOCAGE PHOTO PAR TOKEN DANS L'URL ---
  const processUnlockedPhoto = async () => {
    const urlData = extractTokenFromUrl();
    if (!urlData || tokenProcessed.current) return;
    const { photoId, token } = urlData;
    if (!validateToken(token)) return;
    tokenProcessed.current = true;
    // Corrige l'URL pour garder /voirphoto/{profilId}/ après déblocage
    const profilUnlocked = getProfil(photoId);
    // Correction de l'URL après déblocage : /NomProfil/
    const profilName = profilUnlocked.name;
    window.history.replaceState({}, document.title, `/${encodeURIComponent(profilName)}/`);
    await wait(1000);
    await simulateTyping(setIsTyping, 800);
    const unlockMessage = getRandomFromArray(unlockMessages);
    // Utilise l'avatar du bon profil pour la photo débloquée
    await display(setMessages, { text: unlockMessage, type: "text" });

    // Remplace la photo verrouillée par la photo déverrouillée
    setMessages((prevMessages) => {
      // On cherche la dernière image verrouillée (avec link)
      const idx = prevMessages.findIndex(
        (msg) => msg.type === "image" && msg.link && msg.src === "/images/1.jpg"
      );
      if (idx !== -1) {
        // On remplace par la photo déverrouillée
        const newMessages = [...prevMessages];
        newMessages[idx] = {
          ...newMessages[idx],
          src: `https://voir.photos/t/img/${token}.jpg`,
          alt: "Photo débloquée",
          link: undefined, // plus de lien
        };
        return newMessages;
      }
      // Si pas trouvé, on ajoute quand même la photo déverrouillée (fallback)
      return [
        ...prevMessages,
        {
          type: "image",
          src: `https://voir.photos/t/img/${token}.jpg`,
          alt: "Photo débloquée",
          avatar: getProfil(photoId).avatar,
        },
      ];
    });
  };

  // --- ENVOI D'UN MESSAGE RANDOM (réponse neutre ou érotique) ---
  const sendRandom = async (gib) => {
    const pool = gib ? neutralResponses : eroticResponses;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    await wait(getBotThinkingDelay()); // délai avant réponse
    await display(setMessages, { text: pick, type: "text" });
  };

  // --- FLUX INITIAL DU CHAT (messages d'accueil) ---
  const startInitialFlow = async () => {
    let idx = currentIndex;
    while (idx < mx.messages.length && idx < 5) {
      if (idx > 0) {
        await wait(getRandomDelay());
        await simulateTyping(setIsTyping, 400);
      }
      await display(setMessages, mx.messages[idx]);
      idx++;
    }
    setCurrentIndex(idx);
  };

  // --- ENVOI D'UN MESSAGE UTILISATEUR ---
  const sendUserMessage = async (text) => {
    // Affiche le message utilisateur
    await display(setMessages, { text, type: "text" }, 300, true);

    // Cas 1 : L'utilisateur demande "ça va ?"
    if (isHowAreYou(text)) {
      const randomResponse =
        howAreYouResponses[
        Math.floor(Math.random() * howAreYouResponses.length)
        ];
      await wait(getBotThinkingDelay()); // délai avant typing
      await simulateTyping(setIsTyping, 600);
      await display(setMessages, { text: randomResponse, type: "text" });
      return;
    }

    // Cas 2 : L'utilisateur demande une photo
    if (containsPhotoKeyword(text)) {
      await wait(getBotThinkingDelay()); // délai avant typing
      await simulateTyping(setIsTyping, 1600);
      // Choisir une réponse aléatoire
      const randomPhotoResponse = getRandomFromArray(photoResponses);
      await display(setMessages, {
        text: randomPhotoResponse,
        type: "text"
      });
      await wait(1200);
      // Génère dynamiquement le lien de déblocage avec le bon profilId
      const unlockToken = "FOTO"; // À remplacer par le vrai token généré côté back ou logique métier
      const unlockLink = generateUnlockLink(profilId, unlockToken);
      await display(setMessages, {
        type: "image",
        src: `/images/1.png`,
        alt: "Photo privée",
        link: unlockLink,
        avatar: profil.avatar
      });
      return;
    }

    // Cas 3 : Message incompréhensible ou autre
    const gib = isGibberish(text);
    await wait(getBotThinkingDelay()); // délai avant typing
    await simulateTyping(setIsTyping, 1600);
    await sendRandom(gib);

    // Cas 4 : Affichage du prochain message du flow initial
    if (currentIndex < mx.messages.length) {
      await wait(getBotThinkingDelay()); // délai avant typing
      await simulateTyping(setIsTyping, 1600);
      await display(setMessages, mx.messages[currentIndex]);
      setCurrentIndex((i) => i + 1);
    }
  };

  // --- LANCEMENT DU FLUX INITIAL AU PREMIER RENDU ---
  useEffect(() => {
    if (!initialFlowStarted.current) {
      initialFlowStarted.current = true;
      startInitialFlow();
    }
  }, []);

  // --- TRAITEMENT DU TOKEN DE DÉBLOCAGE AU CHARGEMENT ---
  useEffect(() => {
    processUnlockedPhoto();
  }, []);

  // --- EXPORT DU HOOK ---
  return { messages, isTyping, sendUserMessage };
}
